import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { exportOfferAsPptx } from "@/lib/offer-export";
import { missingBeforeSend } from "@/lib/offer-status-patch";
import {
  useUpdateOffer,
  useDeleteOffer,
  type Offer,
  type OfferLine,
  type OfferStatus,
  type OfferProbability,
} from "@/hooks/use-offers";
import { useDuplicateOffer } from "@/hooks/use-duplicate-offer";
import { useOfferStatusChange } from "@/hooks/use-offer-status-change";
import {
  useDeleteOfferSection,
  useUpdateOfferSection,
  type OfferSection,
} from "@/hooks/use-offer-sections";

interface UseOfferPageHandlersArgs {
  offer: Offer | undefined;
  lines: OfferLine[];
  sections: OfferSection[];
  /**
   * Når false er tilbudet read-only (ikke-forfatter/ikke-admin). Mutasjons-
   * handlere (terms, status, slett, slett seksjon) no-op-er. Eksport (Keynote)
   * er lesing og forblir tillatt. Default true. VIB-offer-author-gating.
   */
  canEdit?: boolean;
  /**
   * Eier/admin. Medredigerer har canEdit men ikke canManage — da no-op-er
   * slett og status (VIB-2099). Default = canEdit.
   */
  canManage?: boolean;
  /**
   * VIB-1304: leses av kalleren via useFeatureFlag("feature_offer_pipeline").
   * Når true settes valid_until (+45d) ved sent og lost_at/lost_reason ved
   * lost (via handleConfirmLost). Når false (default): pre-VIB-1304-oppførsel
   * uendret (kun sent_at/accepted_at). Mørkt bak flagg.
   */
  pipelineEnabled?: boolean;
}

interface UseOfferPageHandlersResult {
  isExportingPptx: boolean;
  handleMainTermsTextChange: (text: string) => void;
  handleSectionTermsTextChange: (sectionId: string, text: string) => void;
  handleDeleteSection: (sectionId: string) => Promise<void>;
  /**
   * Status→lost gir INGEN direkte statusendring her når pipelineEnabled=true
   * — kalleren MÅ fange opp status==="lost" og åpne LostReasonDialog, og
   * committe via handleConfirmLost etter bekreftelse (Erik 15.07-kravet).
   */
  handleStatusChange: (status: OfferStatus) => void;
  /** Committer status->lost med påkrevd begrunnelse (etter LostReasonDialog-bekreftelse). */
  handleConfirmLost: (reason: string) => void;
  /**
   * VIB-2094 (Erik 28.09): sending krever sannsynlighet og estimert
   * close-dato. Kalleren fanger opp "sent" med `missingBeforeSend`, åpner
   * SendOfferDialog, og committer via denne — samme mønster som lost.
   */
  handleConfirmSend: (fields: {
    probability: OfferProbability;
    expectedCloseDate: string;
  }) => void;
  /** VIB-1304, bak feature_offer_pipeline. */
  handleProbabilityChange: (probability: OfferProbability) => void;
  /** VIB-1304, bak feature_offer_pipeline. null = fjern manuell overstyring. */
  handleValidUntilChange: (date: string | null) => void;
  handleDelete: () => Promise<void>;
  handleDuplicate: () => Promise<void>;
  isDuplicating: boolean;
  handleExportPptx: () => Promise<void>;
}

/**
 * Konsoliderer page-level handlers for OfferEditPage som ikke hører hjemme
 * i draft-state eller line-actions: terms-tekst, slett seksjon, status,
 * slett tilbud, eksporter Keynote. Splittet ut fra page (VIB-658) for å
 * holde page-orkestratoren under 300 linjer.
 */
export function useOfferPageHandlers({
  offer,
  lines,
  sections,
  canEdit = true,
  canManage,
  pipelineEnabled = false,
}: UseOfferPageHandlersArgs): UseOfferPageHandlersResult {
  const manage = canManage ?? canEdit;
  const navigate = useNavigate();
  const updateOffer = useUpdateOffer();
  const deleteOffer = useDeleteOffer();
  const duplicateOffer = useDuplicateOffer();
  const { changeStatus } = useOfferStatusChange(pipelineEnabled);
  const deleteSection = useDeleteOfferSection(offer?.id);
  const updateSection = useUpdateOfferSection(offer?.id);
  const [isExportingPptx, setIsExportingPptx] = useState(false);

  const handleMainTermsTextChange = useCallback(
    (text: string) => {
      if (!canEdit) return;
      if (!offer) return;
      updateOffer.mutate({
        id: offer.id,
        patch: { custom_terms_text: text.trim() || null },
      });
    },
    [canEdit, offer, updateOffer],
  );

  const handleSectionTermsTextChange = useCallback(
    (sectionId: string, text: string) => {
      if (!canEdit) return;
      updateSection.mutate({
        id: sectionId,
        patch: { custom_terms_text: text.trim() || null },
      });
    },
    [canEdit, updateSection],
  );

  const handleDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!canEdit) return;
      const section = sections.find((s) => s.id === sectionId);
      const label = section?.name ?? "tilvalget";
      if (!window.confirm(`Fjern ${label}? Linjer flyttes til hovedbudsjettet.`)) return;
      try {
        await deleteSection.mutateAsync(sectionId);
        toast.success(`${label} fjernet`);
      } catch (err) {
        toast.error(`Kunne ikke fjerne: ${(err as Error).message}`);
      }
    },
    [canEdit, sections, deleteSection],
  );

  const handleStatusChange = useCallback(
    (status: OfferStatus) => {
      if (!manage) return;
      if (!offer) return;
      // VIB-1304 (Erik 15.07): status->lost krever en begrunnelse og går
      // ALDRI direkte gjennom denne veien når pipelinen er på — kalleren
      // (OfferEditPage) fanger opp status==="lost" FØR den kaller denne
      // funksjonen og åpner LostReasonDialog i stedet. Denne guarden er et
      // sikkerhetsnett (buildStatusPatch kaster uansett hvis reason mangler).
      if (pipelineEnabled && status === "lost") return;
      // VIB-2094 (Erik 28.09): samme sikkerhetsnett for sending. Mangler
      // sannsynlighet eller close-dato, skal kalleren ha åpnet SendOfferDialog
      // og committe via handleConfirmSend. Vi no-op-er heller enn å la
      // buildStatusPatch kaste, slik at en flate som glemmer dialogen gir et
      // kort som blir liggende — ikke et uhåndtert unntak hos brukeren.
      if (pipelineEnabled && status === "sent" && missingBeforeSend(offer)) return;
      changeStatus(offer, status);
    },
    [manage, offer, pipelineEnabled, changeStatus],
  );

  const handleConfirmLost = useCallback(
    (reason: string) => {
      if (!manage) return;
      if (!offer) return;
      changeStatus(offer, "lost", { lostReason: reason });
    },
    [manage, offer, changeStatus],
  );

  /**
   * VIB-2094: sender tilbudet med sannsynlighet og close-dato fra
   * SendOfferDialog. De skrives i SAMME patch som statusen, så et tilbud aldri
   * kan stå som sendt uten dem, heller ikke hvis nettverket faller mellom to
   * kall.
   */
  const handleConfirmSend = useCallback(
    (fields: { probability: OfferProbability; expectedCloseDate: string }) => {
      if (!manage) return;
      if (!offer) return;
      changeStatus(offer, "sent", { sendFields: fields });
    },
    [manage, offer, changeStatus],
  );

  const handleProbabilityChange = useCallback(
    (probability: OfferProbability) => {
      if (!canEdit) return;
      if (!offer) return;
      updateOffer.mutate({ id: offer.id, patch: { probability } });
    },
    [canEdit, offer, updateOffer],
  );

  const handleValidUntilChange = useCallback(
    (date: string | null) => {
      if (!canEdit) return;
      if (!offer) return;
      updateOffer.mutate({ id: offer.id, patch: { valid_until: date } });
    },
    [canEdit, offer, updateOffer],
  );

  const handleDelete = useCallback(async () => {
    if (!manage) return;
    if (!offer) return;
    if (!window.confirm(`Slett tilbudet "${offer.title}"?`)) return;
    try {
      await deleteOffer.mutateAsync(offer.id);
      toast.success("Tilbud slettet");
      navigate("/tilbud");
    } catch (err) {
      toast.error(`Kunne ikke slette: ${(err as Error).message}`);
    }
  }, [manage, offer, deleteOffer, navigate]);

  const handleDuplicate = useCallback(async () => {
    if (!offer) return;
    try {
      const copy = await duplicateOffer.mutateAsync(offer.id);
      toast.success("Tilbud duplisert");
      navigate(`/tilbud/${copy.id}`);
    } catch (err) {
      toast.error(`Kunne ikke duplisere: ${(err as Error).message}`);
    }
  }, [offer, duplicateOffer, navigate]);

  const handleExportPptx = useCallback(async () => {
    if (!offer) return;
    if (isExportingPptx) return;
    setIsExportingPptx(true);
    try {
      await exportOfferAsPptx(offer, lines, sections);
      toast.success("Keynote lastet ned");
    } catch (err) {
      toast.error(`Kunne ikke eksportere: ${(err as Error).message}`);
    } finally {
      setIsExportingPptx(false);
    }
  }, [offer, lines, sections, isExportingPptx]);

  return {
    isExportingPptx,
    handleMainTermsTextChange,
    handleSectionTermsTextChange,
    handleDeleteSection,
    handleStatusChange,
    handleConfirmLost,
    handleConfirmSend,
    handleProbabilityChange,
    handleValidUntilChange,
    handleDelete,
    handleDuplicate,
    isDuplicating: duplicateOffer.isPending,
    handleExportPptx,
  };
}
